// See NotificationBar.spec.md for NB-FR-001

import { createContext, type ReactNode, useContext, useMemo, useState } from 'react'

// NB-FR-001: Height of the admin notification bar (including an open drawer), so elements fixed to
// the bottom of the window (the grid's floating batch action bar, upload/ISO/support-bundle
// progress cards, the task tray) can sit above it. 0 when there is no bar.
const NotificationBarOffsetContext = createContext(0)
const SetNotificationBarOffsetContext = createContext<(height: number) => void>(() => {})

// Mounted once above the app frame and every bottom-fixed overlay (AuthContainer), so overlays
// rendered outside the frame see the offset too; the frame reports the bar's height through
// useSetNotificationBarOffset
export function NotificationBarOffsetRoot({ children }: { children: ReactNode }) {
  const [offset, setOffset] = useState(0)
  return (
    <SetNotificationBarOffsetContext.Provider value={setOffset}>
      <NotificationBarOffsetContext.Provider value={offset}>
        {children}
      </NotificationBarOffsetContext.Provider>
    </SetNotificationBarOffsetContext.Provider>
  )
}

// A fixed offset, for tests and isolated renders
export const NotificationBarOffsetProvider = NotificationBarOffsetContext.Provider

export const useNotificationBarOffset = () => useContext(NotificationBarOffsetContext)

export const useSetNotificationBarOffset = () => useContext(SetNotificationBarOffsetContext)

// NB-FR-001: Style props for an overlay fixed to the bottom of the window. Pass the result to the
// overlay's makeStyles hook and use `aboveNotificationBar` for its `bottom`.
export interface NotificationBarOffsetProps {
  barOffset: number
}

// The same object until the offset changes, so the overlay's dynamic styles update only then
export const useNotificationBarOffsetProps = (): NotificationBarOffsetProps => {
  const barOffset = useNotificationBarOffset()
  return useMemo(() => ({ barOffset }), [barOffset])
}

// NB-FR-001: A `bottom` style that keeps `gap` px between the overlay and the bar (or the window
// edge when there is no bar)
export const aboveNotificationBar =
  (gap: number) =>
  ({ barOffset }: NotificationBarOffsetProps): number =>
    barOffset + gap
